import { Send } from 'lucide-react';
import { Button, Labeled, TextArea, TextInput } from '@tes/ui';
import { MESSAGE_MAX, SUBJECT_MAX, checkQuickSend, defaultTitle } from '../../lib/quickSend';
import { useQuickSend } from './quickSendStore';

/**
 * Who it goes to and what the invite email says. n8n sends that email off the
 * envelope.sent webhook; the subject and note travel with it.
 */
export function SendForm({ attempted, onReview }: { attempted: boolean; onReview: () => void }) {
  const draft = useQuickSend();
  const { name, email, subject, message, title, setForm } = draft;
  // Only nag once they've tried to send; after that, the list clears as they fix things.
  const errors = attempted ? checkQuickSend(draft).errors : [];

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold">Send to</h2>
      <div className="grid gap-3 md:grid-cols-2">
        <Labeled label="Contact name">
          <TextInput
            value={name}
            onChange={(e) => setForm({ name: e.target.value })}
            placeholder="Jane Citizen"
            autoComplete="off"
          />
        </Labeled>
        <Labeled label="Email">
          <TextInput
            type="email"
            value={email}
            onChange={(e) => setForm({ email: e.target.value })}
            placeholder="jane@example.com"
            autoComplete="off"
          />
        </Labeled>
        <Labeled label="Email subject" className="md:col-span-2">
          <TextInput
            value={subject}
            maxLength={SUBJECT_MAX}
            onChange={(e) => setForm({ subject: e.target.value })}
          />
        </Labeled>
        <Labeled
          label="Note (optional)"
          hint="Shown in the email above the Sign button, as one paragraph."
          className="md:col-span-2"
        >
          <TextArea
            value={message}
            maxLength={MESSAGE_MAX}
            rows={3}
            onChange={(e) => setForm({ message: e.target.value })}
            placeholder="Hi Jane, here are the forms we talked about — it only takes a couple of minutes."
          />
        </Labeled>
        <Labeled
          label="Envelope title"
          hint="How it's listed in Envelopes and named in Google Drive."
          className="md:col-span-2"
        >
          <TextInput
            value={title}
            onChange={(e) => setForm({ title: e.target.value })}
            placeholder={defaultTitle(name)}
          />
        </Labeled>
      </div>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        {errors.length > 0 ? (
          <ul className="list-disc pl-5 text-sm text-red-600">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        ) : (
          <span />
        )}
        <Button onClick={onReview}>
          <Send size={16} className="mr-1" /> Review &amp; send
        </Button>
      </div>
    </section>
  );
}
